import React, { useEffect, useState } from 'react';
import {
  SelectListFilter,
  AppInput,
  AppButton,
  AppColors,
  AppDatePicker,
  SelectOption,
  RoomType,
} from '@giflo/shared';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseFormProps } from './base-forms';
import { FacilitySearchFormData } from '@giflo/shared';
import { FormLabel, Flex } from '@chakra-ui/react';
import { addDays } from 'date-fns';
import { UTCDate } from '@date-fns/utc';
import { useDispatch, useSelector } from 'react-redux';
import { selectBookingRoomType, setBookingDuration } from '../../store/slices/booking.slice';

const facilitySearchFormDefaultValues: FacilitySearchFormData = {
  date: new Date(),
  capacity: 1,
  duration: undefined,
};

const facilitySearchFormDataSchema = yup.object({
  date: yup.date(),
  capacity: yup.number(),
  duration: yup.number(),
});

type FacilitySearchFormProps<T> = {
  form?: FacilitySearchFormData;
  onSearch?: () => void;
  handleFirstAvailableSlot: () => void;
} & BaseFormProps<T>;

const FacilitySearchForm: React.FC<
  FacilitySearchFormProps<FacilitySearchFormData>
> = ({ form, onSubmit, handleFirstAvailableSlot }) => {
  const [defaultDurationOption, setDefaultDurationOption] =
    useState<SelectOption>();

  const {
    control: control,
    formState: { isValid, errors },
    handleSubmit,
    setValue,
  } = useForm<FacilitySearchFormData>({
    defaultValues: form || facilitySearchFormDefaultValues,
    resolver: yupResolver(facilitySearchFormDataSchema),
    mode: 'onChange',
  });

  const dispatch = useDispatch();
  const currentRoomType = useSelector(selectBookingRoomType);

  const durations = [15, 30, 45, 1, 2, 3, 4, 5, 6, 7, 8];
  const [durationOptions, setDuationOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (currentRoomType === RoomType.MEETING_ROOM) {
      const selectors: SelectOption[] = [];
      durations.map((x) => {
        selectors.push({
          label: x > 10 ? `${x}min` : `${x} hr`,
          value: x,
        });
      });
      setDuationOptions(selectors);

      setDefaultDurationOption({
        label: `15 min`,
        value: 15,
      } as SelectOption);
    }
  }, [currentRoomType]);

  const handleDurationSelect = (item: SelectOption) => {
    setValue('duration', item.value);
    dispatch(setBookingDuration(item.value))
  };

  return (
    <>
      {currentRoomType === RoomType.MEETING_ROOM ? (
        <Flex
          gap={{ base: 3, lg: 6 }}
          direction={{ base: 'column', lg: 'row' }}
          py={4}
          w={'full'}
          align={'end'}
          justify={'center'}
        >
          <Flex direction={'column'} w={'full'}>
            <FormLabel fontSize={'sm'} fontWeight={'600'}>
              DATE
            </FormLabel>
            <AppDatePicker<FacilitySearchFormData>
              name='date'
              minDate={addDays(new UTCDate(), -1)}
              control={control}
              error={undefined}
              label=''
            />
          </Flex>
          <Flex direction={'column'} w={'full'}>
            <FormLabel fontSize={'sm'} fontWeight={'600'}>
              CAPACITY
            </FormLabel>
            <AppInput<FacilitySearchFormData>
              name='capacity'
              error={errors.capacity}
              control={control}
              label=''
              inputType='number'
            />
          </Flex>
          <Flex direction={'column'} w={'full'}>
            <FormLabel fontSize={'sm'} fontWeight={'600'}>
              DURATION
            </FormLabel>
            <SelectListFilter
              mt={2}
              options={durationOptions}
              name='duration'
              isInModal
              isMulti={false}
              placeholder='Duration'
              showClear={false}
              defaultOption={defaultDurationOption}
              onSelectionChange={(item: SelectOption[]) => {
                handleDurationSelect(item[0]);
              }}
            />
          </Flex>
          <Flex direction={'column'} w={'full'} gap={3}>
            <AppButton
              bgColor={AppColors.tertiary}
              color={'white'}
              onClick={handleSubmit(onSubmit)}
              borderRadius={0}
              size={'md'}
            >
              Search
            </AppButton>
          </Flex>
        </Flex>
      ) : (
        <Flex
          gap={{ base: 3, lg: 6 }}
          direction={{ base: 'column', lg: 'row' }}
          py={4}
          w={'full'}
          align={'end'}
          justify={'center'}
        >
          <Flex direction={'column'} w={'full'} justify={'end'}>
            <FormLabel fontSize={'sm'} fontWeight={'600'}>
              DATE
            </FormLabel>
            <AppDatePicker<FacilitySearchFormData>
              name='date'
              minDate={addDays(new UTCDate(), -1)}
              control={control}
              error={undefined}
              label=''
            />
          </Flex>
          <Flex direction={'column'} w={'full'} gap={3}>
            <AppButton
              bgColor={AppColors.tertiary}
              color={'white'}
              onClick={handleSubmit(onSubmit)}
              borderRadius={0}
              size={'md'}
            >
              Search
            </AppButton>
          </Flex>
        </Flex>
      )}
    </>
  );
};

export default FacilitySearchForm;
